import * as THREE from 'three';

/**
 * ULTRA PERFORMANS: Lazy Geometry Clone
 * Büyük geometry'lerin clone işleminde donmayı önler
 *
 * Problem:
 * - Büyük mesh'lerde geometry.clone() 100-500ms sürebilir
 * - Cetvel/ölçüm ilk seçiminde UI donuyor
 * - sonSecilenHighlightGeometriYuzey için full clone gerekiyor
 *
 * Çözüm:
 * - Lazy clone: Sadece ihtiyaç olduğunda clone et
 * - Bounding box proxy: İlk seçimde sadece bounds kullan
 * - Progressive clone: Arka planda async clone
 * - Shared geometry: Aynı geometry için tekrar clone yapma
 * - Reference counting: Kullanılmayan geometry'leri temizle
 *
 * Features:
 * - Zero-blocking initial selection
 * - Async geometry preparation
 * - Intelligent caching with LRU eviction
 * - Memory-efficient reference sharing
 */

interface LazyGeometryEntry {
  sourceGeometry: THREE.BufferGeometry;
  clonedGeometry: THREE.BufferGeometry | null;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  triangleCount: number;
  status: 'pending' | 'cloning' | 'ready';
  priority: number;
  lastAccessTime: number;
  refCount: number;
  clonePromise?: Promise<THREE.BufferGeometry>;
}

interface ProxyGeometry {
  type: 'proxy';
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  sourceUuid: string;
}

// Pre-allocated for zero-allocation operations
const _tempBox3 = new THREE.Box3();
const _tempSphere = new THREE.Sphere();

export class FikoLazyGeometryClone {
  // Geometry cache
  private cache = new Map<string, LazyGeometryEntry>();

  // Clone queue
  private cloneQueue: string[] = [];
  private isProcessingQueue = false;

  // Configuration
  private config = {
    largeGeometryThreshold: 10000,      // Triangle count threshold for lazy clone
    maxCacheSize: 100,                   // Maximum cached geometries
    cloneTimeoutMs: 16,                  // Max ms per frame for cloning
    priorityBoostOnAccess: 10,           // Priority boost when accessed
    useProxyForLarge: true,              // Use bounding box proxy for large geometries
    asyncCloneEnabled: true              // Enable async cloning
  };

  // Stats
  private stats = {
    cacheHits: 0,
    cacheMisses: 0,
    lazyClones: 0,
    immediateClones: 0,
    proxyReturns: 0,
    totalCloneTime: 0
  };

  constructor() {
    // Start background processing
    if (typeof requestIdleCallback !== 'undefined') {
      this.scheduleBackgroundCloning();
    }
  }

  /**
   * Get or create geometry for selection highlight
   * Returns immediately - may return proxy for large geometries
   */
  getGeometry(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string,
    needsFullClone: boolean = false
  ): THREE.BufferGeometry | ProxyGeometry {
    const entry = this.cache.get(geometryKey);

    // Cache hit - ready geometry
    if (entry && entry.status === 'ready' && entry.clonedGeometry) {
      entry.lastAccessTime = performance.now();
      entry.refCount++;
      entry.priority += this.config.priorityBoostOnAccess;
      this.stats.cacheHits++;
      return entry.clonedGeometry;
    }

    // Cache hit - still cloning
    if (entry && entry.status === 'cloning') {
      entry.lastAccessTime = performance.now();
      entry.priority += this.config.priorityBoostOnAccess;

      // Return proxy if allowed
      if (this.config.useProxyForLarge && !needsFullClone) {
        this.stats.proxyReturns++;
        return this.createProxy(entry, geometryKey);
      }

      // Wait for clone if needed immediately
      this.stats.cacheMisses++;
      return this.forceImmediateClone(sourceGeometry, geometryKey);
    }

    // Calculate triangle count
    const triangleCount = this.getTriangleCount(sourceGeometry);

    // Small geometry - clone immediately
    if (triangleCount < this.config.largeGeometryThreshold) {
      this.stats.immediateClones++;
      return this.immediateClone(sourceGeometry, geometryKey);
    }

    // Large geometry - use lazy clone
    this.stats.lazyClones++;
    this.stats.cacheMisses++;

    // Create entry and start async clone
    const newEntry = this.createEntry(sourceGeometry, geometryKey, triangleCount);

    // Return proxy immediately
    if (this.config.useProxyForLarge && !needsFullClone) {
      this.queueForCloning(geometryKey);
      this.stats.proxyReturns++;
      return this.createProxy(newEntry, geometryKey);
    }

    // Need full clone now - blocking but necessary
    return this.forceImmediateClone(sourceGeometry, geometryKey);
  }

  /**
   * Get geometry async - returns promise
   */
  async getGeometryAsync(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string
  ): Promise<THREE.BufferGeometry> {
    const entry = this.cache.get(geometryKey);

    // Cache hit - ready
    if (entry && entry.status === 'ready' && entry.clonedGeometry) {
      entry.lastAccessTime = performance.now();
      entry.refCount++;
      return entry.clonedGeometry;
    }

    // Already cloning - wait for it
    if (entry && entry.clonePromise) {
      return entry.clonePromise;
    }

    // Start new async clone
    const triangleCount = this.getTriangleCount(sourceGeometry);
    const newEntry = this.createEntry(sourceGeometry, geometryKey, triangleCount);

    // Create clone promise
    newEntry.clonePromise = this.asyncClone(sourceGeometry, geometryKey);
    return newEntry.clonePromise;
  }

  /**
   * Check if geometry is ready (not proxy)
   */
  isReady(geometryKey: string): boolean {
    const entry = this.cache.get(geometryKey);
    return entry?.status === 'ready';
  }

  /**
   * Get clone progress (0-100)
   */
  getProgress(geometryKey: string): number {
    const entry = this.cache.get(geometryKey);
    if (!entry) return 0;
    if (entry.status === 'ready') return 100;
    if (entry.status === 'cloning') return 50; // Approximate
    return 0;
  }

  /**
   * Create cache entry
   */
  private createEntry(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string,
    triangleCount: number
  ): LazyGeometryEntry {
    // Compute bounds (cheap)
    if (!sourceGeometry.boundingBox) {
      sourceGeometry.computeBoundingBox();
    }
    if (!sourceGeometry.boundingSphere) {
      sourceGeometry.computeBoundingSphere();
    }

    const entry: LazyGeometryEntry = {
      sourceGeometry,
      clonedGeometry: null,
      boundingBox: sourceGeometry.boundingBox!.clone(),
      boundingSphere: sourceGeometry.boundingSphere!.clone(),
      triangleCount,
      status: 'pending',
      priority: triangleCount < 50000 ? 100 : 50, // Smaller = higher priority
      lastAccessTime: performance.now(),
      refCount: 1
    };

    this.cache.set(geometryKey, entry);
    this.enforceMaxCacheSize();

    return entry;
  }

  /**
   * Create proxy geometry (just bounds)
   */
  private createProxy(entry: LazyGeometryEntry, sourceUuid: string): ProxyGeometry {
    return {
      type: 'proxy',
      boundingBox: entry.boundingBox.clone(),
      boundingSphere: entry.boundingSphere.clone(),
      sourceUuid
    };
  }

  /**
   * Immediate clone (blocking)
   */
  private immediateClone(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string
  ): THREE.BufferGeometry {
    const startTime = performance.now();

    const cloned = sourceGeometry.clone();

    const cloneTime = performance.now() - startTime;
    this.stats.totalCloneTime += cloneTime;

    // Cache it
    const triangleCount = this.getTriangleCount(sourceGeometry);
    const entry: LazyGeometryEntry = {
      sourceGeometry,
      clonedGeometry: cloned,
      boundingBox: cloned.boundingBox || new THREE.Box3(),
      boundingSphere: cloned.boundingSphere || new THREE.Sphere(),
      triangleCount,
      status: 'ready',
      priority: 100,
      lastAccessTime: performance.now(),
      refCount: 1
    };

    this.cache.set(geometryKey, entry);

    return cloned;
  }

  /**
   * Force immediate clone (for when we really need it now)
   */
  private forceImmediateClone(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string
  ): THREE.BufferGeometry {

    const entry = this.cache.get(geometryKey);
    if (entry && entry.clonedGeometry) {
      return entry.clonedGeometry;
    }

    return this.immediateClone(sourceGeometry, geometryKey);
  }

  /**
   * Async clone using requestIdleCallback
   */
  private async asyncClone(
    sourceGeometry: THREE.BufferGeometry,
    geometryKey: string
  ): Promise<THREE.BufferGeometry> {
    const entry = this.cache.get(geometryKey);
    if (!entry) {
      return sourceGeometry.clone();
    }

    entry.status = 'cloning';

    return new Promise((resolve) => {
      const doClone = () => {
        const startTime = performance.now();
        const cloned = sourceGeometry.clone();
        const cloneTime = performance.now() - startTime;

        this.stats.totalCloneTime += cloneTime;

        entry.clonedGeometry = cloned;
        entry.status = 'ready';
        entry.lastAccessTime = performance.now();

        resolve(cloned);
      };

      // Use idle callback for non-blocking
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(doClone, { timeout: 1000 });
      } else {
        setTimeout(doClone, 0);
      }
    });
  }

  /**
   * Queue geometry for background cloning
   */
  private queueForCloning(geometryKey: string): void {
    if (!this.cloneQueue.includes(geometryKey)) {
      this.cloneQueue.push(geometryKey);
      this.cloneQueue.sort((a, b) => {
        const entryA = this.cache.get(a);
        const entryB = this.cache.get(b);
        return (entryB?.priority || 0) - (entryA?.priority || 0);
      });
    }

    if (!this.isProcessingQueue) {
      this.processCloneQueue();
    }
  }

  /**
   * Process clone queue in background
   */
  private async processCloneQueue(): Promise<void> {
    if (this.isProcessingQueue || this.cloneQueue.length === 0) return;

    this.isProcessingQueue = true;

    while (this.cloneQueue.length > 0) {
      const key = this.cloneQueue.shift()!;
      const entry = this.cache.get(key);

      if (entry && entry.status === 'pending') {
        entry.status = 'cloning';

        // Clone with yield
        await new Promise<void>((resolve) => {
          const doClone = (deadline?: IdleDeadline) => {
            const startTime = performance.now();
            const cloned = entry.sourceGeometry.clone();
            const cloneTime = performance.now() - startTime;

            this.stats.totalCloneTime += cloneTime;

            entry.clonedGeometry = cloned;
            entry.status = 'ready';

            resolve();
          };

          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(doClone, { timeout: 500 });
          } else {
            setTimeout(doClone, 0);
          }
        });
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Schedule background cloning during idle time
   */
  private scheduleBackgroundCloning(): void {
    const processIdle = () => {
      this.processCloneQueue();

      // Reschedule
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(processIdle, { timeout: 5000 });
      }
    };

    requestIdleCallback(processIdle, { timeout: 5000 });
  }

  /**
   * Get triangle count
   */
  private getTriangleCount(geometry: THREE.BufferGeometry): number {
    if (geometry.index) {
      return geometry.index.count / 3;
    }
    const position = geometry.getAttribute('position');
    return position ? position.count / 3 : 0;
  }

  /**
   * Enforce max cache size using LRU.
   * Only evicts entries with refCount <= 0 to avoid disposing geometries
   * that are still attached to active meshes.
   */
  private enforceMaxCacheSize(): void {
    if (this.cache.size <= this.config.maxCacheSize) return;

    // Sort by last access time (oldest first), but only consider entries
    // that are not actively referenced (refCount <= 0)
    const evictable = Array.from(this.cache.entries())
      .filter(([, entry]) => entry.refCount <= 0)
      .sort((a, b) => a[1].lastAccessTime - b[1].lastAccessTime);

    // Remove oldest unreferenced entries until we're under the limit
    const excess = this.cache.size - this.config.maxCacheSize;
    const toRemove = evictable.slice(0, excess);

    for (const [key, entry] of toRemove) {
      if (entry.clonedGeometry) {
        entry.clonedGeometry.dispose();
      }
      this.cache.delete(key);
    }
  }

  /**
   * Release geometry reference. Decrements refCount but never below 0.
   * Entry stays in cache for LRU reuse; only evicted when refCount is 0.
   */
  release(geometryKey: string): void {
    const entry = this.cache.get(geometryKey);
    if (entry) {
      entry.refCount = Math.max(0, entry.refCount - 1);

      // Don't delete immediately - let LRU handle it
    }
  }

  /**
   * Pre-warm cache for visible geometries
   */
  prewarm(geometries: Array<{ geometry: THREE.BufferGeometry; key: string }>): void {
    for (const { geometry, key } of geometries) {
      if (!this.cache.has(key)) {
        const triangleCount = this.getTriangleCount(geometry);
        this.createEntry(geometry, key, triangleCount);
        this.queueForCloning(key);
      }
    }
  }

  /**
   * Get statistics
   */
  getStats(): typeof this.stats & {
    cacheSize: number;
    queueLength: number;
    avgCloneTime: string;
  } {
    const totalClones = this.stats.immediateClones + this.stats.lazyClones;
    const avgTime = totalClones > 0
      ? (this.stats.totalCloneTime / totalClones).toFixed(2)
      : '0';

    return {
      ...this.stats,
      cacheSize: this.cache.size,
      queueLength: this.cloneQueue.length,
      avgCloneTime: `${avgTime}ms`
    };
  }

  /**
   * Configure
   */
  configure(config: Partial<typeof this.config>): void {
    Object.assign(this.config, config);
  }

  /**
   * Clear all cache
   */
  clear(): void {
    for (const entry of this.cache.values()) {
      if (entry.clonedGeometry) {
        entry.clonedGeometry.dispose();
      }
    }
    this.cache.clear();
    this.cloneQueue = [];
  }

  /**
   * Dispose
   */
  dispose(): void {
    this.clear();
  }
}

/**
 * Helper: Check if result is proxy
 */
export function isProxyGeometry(result: THREE.BufferGeometry | ProxyGeometry): result is ProxyGeometry {
  return (result as ProxyGeometry).type === 'proxy';
}

/**
 * Global singleton instance
 */
let _lazyGeometryCloneInstance: FikoLazyGeometryClone | null = null;

export function getLazyGeometryClone(): FikoLazyGeometryClone {
  if (!_lazyGeometryCloneInstance) {
    _lazyGeometryCloneInstance = new FikoLazyGeometryClone();
  }
  return _lazyGeometryCloneInstance;
}
